import { fetchAllCategories, fetchCategoriesByIds } from '@/redux/api/category';
import { loadBatchEnd, loadBatchStart } from './fetchBatch';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from '@reduxjs/toolkit';
import difference from 'lodash/difference';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_CATEGORIES_FAIL = 'la/domain/category/LOAD_FAIL';
export const LOAD_CATEGORIES_REQUEST = 'la/domain/category/LOAD_REQUEST';
export const LOAD_CATEGORIES_SUCCESS = 'la/domain/category/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');
const FETCH_SET_SIZE = 120;

/* reducer */
export const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_CATEGORIES_FAIL:
            return {
                ...state,
                loading: difference(state.loading, action.meta?.categoryIds),
            };
        case LOAD_CATEGORIES_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case LOAD_CATEGORIES_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = cloneDeep(state.loaded);
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (Array.isArray(action.payload.payload) && !action.payload?.isAllCategories) {
                action.payload.payload.forEach((cat) => {
                    existing[cat.categoryId] = cat;
                    loaded[cat.categoryId] = time;
                    loading = difference(loading, [cat.categoryId]);
                });
            } else if (action.payload?.isAllCategories) {
                loaded = action.payload.payload;
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.category;
const idSelector = (state: GlobalState, id) => id;

export const byIdSelector = createSelector(stateSelector, (state) => state.byId);

export const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getCategory: (state: GlobalState, id: any) => any = createSelector(
    [byIdSelector, idSelector],
    (byId, id) => byId[id] || {}
);

const whichCategoriesNeeded = createSelector(
    [byIdSelector, loadedSelector, loadingSelector, idSelector],
    (ids, loaded, loading, categoryIds) => {
        const needed = [];
        categoryIds.forEach((categoryId: number) => {
            if (categoryId) {
                const time = Date.now();
                const diff = time - loaded[categoryId];
                if (diff < REDUX_STORE_TIME) {
                    return false;
                }
            }
            if (!loading.includes(categoryId)) {
                needed.push(categoryId);
            }
        });
        return needed;
    }
);

/* ACTION CREATORS */
const loadCategories =
    (categoryIds = [], loadingId, isFinalFetch) =>
    async (dispatch, getState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            dispatch({
                payload: categoryIds,
                type: LOAD_CATEGORIES_REQUEST,
            });
            const response = await fetchCategoriesByIds({ authToken, categoryIds, deployment });
            const successPromise = dispatch({
                meta: { actionTime: Date.now(), categoryIds },
                payload: response,
                type: LOAD_CATEGORIES_SUCCESS,
            });
            if (isFinalFetch) {
                return dispatch(loadBatchEnd(loadingId));
            } else {
                return successPromise;
            }
        } catch (error) {
            dispatch({
                error: true,
                meta: { categoryIds },
                payload: error,
                type: LOAD_CATEGORIES_FAIL,
            });
            return dispatch(loadBatchEnd(loadingId));
        }
    };

const loadCategoriesInSets = (ids = [], setSize, loadingId) => {
    return (dispatch) => {
        if (ids.length > setSize) {
            return dispatch(loadCategories(ids.slice(0, setSize), loadingId, false)).then(() => {
                dispatch(loadCategoriesInSets(ids.slice(setSize), setSize, loadingId));
            });
        }
        return dispatch(loadCategories(ids, loadingId, true));
    };
};

export const fetchAllCategoriesLandingPageData = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);

    try {
        dispatch({
            type: LOAD_CATEGORIES_REQUEST,
        });
        const response = await fetchAllCategories({ authToken, deployment });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: { ...response, isAllCategories: true },
            type: LOAD_CATEGORIES_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error,
            type: LOAD_CATEGORIES_FAIL,
        });
    }
};

export const fetchCategoriesIfNeeded =
    (categoryIds: number[], loadingId: string = 'none') =>
    async (dispatch: Function, getState: Function) => {
        const needed = whichCategoriesNeeded(getState(), categoryIds);
        if (needed.length) {
            dispatch(loadBatchStart(loadingId));
            return dispatch(loadCategoriesInSets(needed, FETCH_SET_SIZE, loadingId));
        }
        return Promise.resolve();
    };
